import * as React from "react";
import * as mutations from "../graphql/mutations";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StartIcon from "@mui/icons-material/Start";
import DeletionModal from "../components/modals/DeletionModal";
import InvokeGenerationApi from "./InvokeGenerationApi";
import { post } from "aws-amplify/api";
import { apiClient } from "../helpers/apiClient";
import { useTranslation } from "react-i18next";
import {
  fetchAllAssessmentAssignmentsByCustomer,
  fetchAssessmentById,
  fetchCustomersEvaluations,
  fetchDataForEvaluation,
} from "../helpers/apiRequests";
import {
  CGI_ERROR,
  CGI_PURPLE,
  CGI_RED,
  CGI_SUCCESS,
  CGI_WARNING,
  languages,
} from "../helpers/enums";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";

export default function EvaluationSelect({
  selectedCustomer,
  setNotificationMessage,
  setOpenNotification,
  setOpenLoading,
  setSelectedEvaluation,
  setSelectedModule,
  generationOptions = true,
}) {
  const { t } = useTranslation();

  const [customersModules, setCustomersModules] = React.useState([]);

  async function fetchModules() {
    const assignments = await fetchAllAssessmentAssignmentsByCustomer(
      selectedCustomer.id,
    );
    const newCustomersModules = await Promise.all(
      (assignments || []).map(async (assignment) => ({
        ...assignment,
        assessment: await fetchAssessmentById(assignment.assessmentID),
      })),
    );
    setCustomersModules(newCustomersModules);
  }

  const [evaluations, setEvaluations] = React.useState([]);
  async function fetchEvaluations() {
    try {
      const evaluationsData = await fetchCustomersEvaluations(
        selectedCustomer.id
      );
      setEvaluations(evaluationsData);
    } catch (e) {
      // console.log(e);
    }
  }

  async function fetchData() {
    setOpenLoading(true);
    try {
      await fetchModules();
      await fetchEvaluations();
    } catch {
      setNotificationMessage(t("errorFetchDataMessage"));
      setOpenNotification(true);
    }
    setOpenLoading(false);
  }

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    const interval = setInterval(() => {
      fetchEvaluations();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  async function invokeGenerationApi(requestBody) {
    setOpenLoading(true);
    try {
      const apiName = "EvaluationRequestApi";
      const path = "/evaluation";
      const options = { body: requestBody };

      const restOperation = await post({ apiName, path, options });

      const { body } = await restOperation.response;
      const responseText = await body.text();
      let parsed = responseText;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = responseText;
      }

      const message =
        typeof parsed === "string"
          ? parsed
          : parsed && parsed.message
          ? parsed.message
          : JSON.stringify(parsed);

      setNotificationMessage(t(message));
      try {
        const evaluationsData = await fetchCustomersEvaluations(
          selectedCustomer.id
        );
        setEvaluations(evaluationsData);
      } catch (refreshError) {
        console.error(refreshError);
      }
    } catch (e) {
      console.error(e);
      setNotificationMessage(t("errorMessage"));
    } finally {
      setOpenNotification(true);
      setOpenLoading(false);
    }
  }

  async function deleteEvaluation(toDeleteId) {
    try {
      await apiClient.graphql({
        query: mutations.deleteEvaluation,
        variables: {
          input: {
            id: toDeleteId,
          },
        },
      });

      const evaluationsData = await fetchDataForEvaluation(toDeleteId);

      await Promise.all(
        evaluationsData.map((data) =>
          apiClient.graphql({
            query: mutations.deleteEvaluationModuleData,
            variables: {
              input: {
                id: data.id,
              },
            },
          })
        )
      );

      fetchEvaluations();
      setNotificationMessage(t("deletedEvaluation"));
    } catch {
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  const [openDelete, setOpenDelete] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState("");

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mt: { lg: 8, xs: 3 },
          mx: { lg: 5, xs: 0.5 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 500, color: CGI_RED }}>
            {generationOptions
              ? t("selectOrGenerateEvaluation")
              : t("selectAssessment")}
          </Typography>
        </Box>
        <Divider
          flexItem
          sx={{ mt: 1, mb: 2, color: CGI_RED, borderBottom: 3 }}
        />
        {customersModules.map((module, index) => (
          <Box key={index} sx={{ mx: { xs: 0.2, md: 2 }, mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
                pb: 1,
                borderBottom: 1,
                borderColor: "#aaa",
                width: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", lg: "row" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Typography sx={{ fontSize: 16, mr: 1 }}>
                    Assessment:
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 500, fontSize: 16, width: 300 }}
                  >
                    {module.assessment?.name} | {module.assessment?.language} (
                    {module.package})
                  </Typography>
                </Box>
              </Box>
              {generationOptions && (
                <InvokeGenerationApi
                  invokeGenerationApi={invokeGenerationApi}
                  selectedCustomer={selectedCustomer}
                  module={module}
                />
              )}
            </Box>
            {evaluations.filter(
              (evaluation) => evaluation.assessmentAssignmentID === module.id
            ).length <= 0 && (
              <Typography sx={{ ml: 3 }}>
                {t("noEvaluationAvailable")}
              </Typography>
            )}
            {evaluations
              .filter(
                (evaluation) => evaluation.assessmentAssignmentID === module.id
              )
              .map((evaluation, index) => {
                const evaluationDate = new Date(evaluation.createdAt);
                return (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: 1,
                      borderColor: "#999",
                      ml: { lg: 3, xs: 0.2 },
                      mr: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      {generationOptions && (
                        <IconButton
                          sx={{ mb: 0.35, color: CGI_RED }}
                          onClick={() => {
                            setToDeleteId(evaluation.id);
                            setOpenDelete(true);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                      <Typography>
                        {t("evaluationFrom-")}
                        {evaluationDate.toLocaleString(t("timeSettings"), {
                          timeZone: "Europe/Berlin",
                        })}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        border: 2,
                        borderRadius: 5,
                        px: 0.7,
                        fontWeight: 500,
                        borderColor: CGI_PURPLE,
                        color: CGI_PURPLE,
                        minWidth: 40,
                        ml: 2,
                        userSelect: "none",
                      }}
                      align="center"
                    >
                      {evaluation?.language || languages.DE}
                    </Typography>
                    <Typography sx={{ width: 370, ml: 2 }}>
                      id: {evaluation.id}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        border: 2,
                        borderRadius: 20,
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 100,
                        width: 120,
                        color:
                          evaluation.status === "RUNNING"
                            ? CGI_WARNING
                            : evaluation.status === "FINISHED"
                            ? CGI_SUCCESS
                            : CGI_ERROR,
                      }}
                    >
                      {evaluation.status === "RUNNING" && (
                        <CircularProgress size={15} sx={{ mr: 1 }} />
                      )}
                      <Typography
                        sx={{
                          fontWeight: 500,
                          userSelect: "none",
                          textAlign: "center",
                        }}
                      >
                        {evaluation.status}
                      </Typography>
                    </Box>
                    <Button
                      disabled={evaluation.status !== "FINISHED"}
                      color="secondary"
                      sx={{
                        borderRadius: 0,
                        px: 1,
                        ml: 2.5,
                        borderLeft: 1,
                        textTransform: "none",
                        minWidth: generationOptions ? 150 : 200,
                      }}
                      endIcon={<StartIcon />}
                      onClick={() => {
                        setSelectedEvaluation(evaluation);
                        if (generationOptions) {
                          setSelectedModule(
                            module.assessment?.name +
                              " (" +
                              module.package +
                              ")"
                          );
                        } else {
                          setSelectedModule({
                            id: module.assessmentID,
                            assessmentAssignmentID: module.id,
                            name: module.assessment?.name,
                            title: module.assessment?.name,
                            package: module.package,
                            language: module.assessment?.language,
                          });
                        }
                      }}
                    >
                      {generationOptions
                        ? t("goToEvaluation")
                        : t("selectEvaluation")}
                    </Button>
                  </Box>
                );
              })}
          </Box>
        ))}
      </Box>
      <DeletionModal
        open={openDelete}
        setOpen={setOpenDelete}
        deleteFunction={deleteEvaluation}
        safeDelete={true}
        itemClass="Evaluation"
        id={toDeleteId}
      />
    </>
  );
}
